/**
 * Pure decision for the PLP post-hydration reconciliation (ECOM-2692).
 *
 * The browser-back-into-hydration race can leave a PLP whose category data
 * and/or product slice never loaded for the active route. `usePlpNavigationReconcile`
 * calls this after hydration (and on each navigation) to decide whether to run
 * the full `useNewPlp.fetch()` to self-heal.
 *
 * Kept pure (no Vue/Nuxt imports) so the truth table — especially the
 * over-fire and terminal-404 guards — is exhaustively unit-testable without a
 * DOM or Nuxt context (the jest harness here cannot mount SFCs/composables).
 */
export type ReconcileInputs = {
  /** Is this a client-execution PLP (products fetched client-side, not SSR'd)?
   * Covers both the Odoscope and Scayle-fallback paths — the reconcile replaces
   * the old onMounted client fetch, which fired for every client-execution PLP. */
  isPlpEnabled: boolean
  /** Has client hydration completed? Gated on the app:suspense:resolve signal. */
  isHydrated: boolean
  /** Is any product fetch already running for this route (full fetch() or a
   * source-specific products fetch)? Gates re-entry so concurrent triggers
   * don't issue duplicate work. */
  isFetchInFlight: boolean
  /** Has the active route's product slice completed a real fetch? (slice.fetched) */
  fetched: boolean
  /**
   * Does the active route currently have products rendered? Defensive guard:
   * today products only appear after `fetched` is true, so this is dominated by
   * `!fetched` — but keeping it preserves correctness if products can ever be
   * present without a completed fetch (e.g. a future SSR/cache path).
   */
  hasProducts: boolean
  /** Has the active route's category (activeNode) resolved? */
  hasCategoryData: boolean
  /**
   * Has a category fetch for the active route *completed* (status !== 'idle')?
   * Distinguishes "category not yet attempted" (reconcile) from "category
   * attempted and confirmed absent" — a genuine 404 — which is terminal and
   * must NOT keep re-arming reconciliation (R5 applied to the category half).
   */
  categoryResolutionAttempted: boolean
}

export const shouldReconcilePlp = ({
  isPlpEnabled,
  isHydrated,
  isFetchInFlight,
  fetched,
  hasProducts,
  hasCategoryData,
  categoryResolutionAttempted,
}: ReconcileInputs): boolean => {
  // Only reconcile a hydrated, idle client-execution PLP. Before hydration the
  // normal first fetch owns the page; while a fetch is in flight a second one
  // would be a duplicate.
  if (!isPlpEnabled || !isHydrated || isFetchInFlight) {
    return false
  }

  // Terminal: a category that was attempted and came back absent is a real
  // 404. Let it surface — do not paper over a genuinely-missing category with
  // a per-navigation retry loop.
  if (!hasCategoryData && categoryResolutionAttempted) {
    return false
  }

  // The product slice never completed a fetch and the listing is empty — the
  // stranded-products half of the bug.
  const needsProducts = !hasProducts && !fetched

  // Category data never materialized (and, per the guard above, has not yet
  // been attempted) — the stranded-category half. Fires independently of the
  // product slice so a missing title/meta is also healed (R6).
  const needsCategory = !hasCategoryData

  return needsProducts || needsCategory
}
